import { feathers } from '@feathersjs/feathers';
import rest from '@feathersjs/rest-client';
import authentication from '@feathersjs/authentication-client';
import { appConfig } from './config/app.config';
import type { Application } from '@feathersjs/feathers';

let app: Application | null = null;

// Function to initialize the Feathers client
const initializeFeathersClient = (): Application => {
  if (app) {
    return app;
  }

  // Create the Feathers app
  app = feathers();

  // Get the API URL from environment variables
  const apiUrl = appConfig.API_ENDPOINT;

  // Connect to the REST transport
  const restClient = rest(apiUrl);
  app.configure(restClient.fetch(window.fetch.bind(window)));

  // Configure authentication
  app.configure(authentication({
    storage: window.localStorage,
    storageKey: 'feathers-jwt',
    path: '/authentication',
    jwtStrategy: 'jwt',
    locationKey: 'access_token',
    locationErrorKey: 'error'
  }));

  return app;
};

// Function to get the Feathers client (lazy initialization)
export const getFeathersClient = (): Application => {
  return initializeFeathersClient();
};

// For backward compatibility, export a getter that initializes on first access
export default new Proxy({} as Application, {
  get(target, prop, receiver) {
    const client = initializeFeathersClient();
    const value = Reflect.get(client, prop, receiver);
    return typeof value === 'function' ? value.bind(client) : value;
  },
  set(target, prop, value, receiver) {
    const client = initializeFeathersClient();
    return Reflect.set(client, prop, value, receiver);
  },
  has(target, prop) {
    const client = initializeFeathersClient();
    return Reflect.has(client, prop);
  },
  ownKeys(target) {
    const client = initializeFeathersClient();
    return Reflect.ownKeys(client);
  },
  getOwnPropertyDescriptor(target, prop) {
    const client = initializeFeathersClient();
    return Reflect.getOwnPropertyDescriptor(client, prop);
  }
}); 